import { useState } from "react";
import { Fingerprint, Pencil, Trash2, Check, X, ShieldCheck, Monitor } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserPasskeys, useRenamePasskey } from "@/hooks/auth/usePasskeyQueries";
import DeletePasskeyDialog from "./DeletePasskeyDialog";

/**
 * Format an ISO date string into a readable short date.
 * Returns "Never" for null/undefined values (e.g. lastUsedAt before first use).
 */
const formatDate = (iso) => {
    if (!iso) return "Never";
    return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

/**
 * DeviceTypeBadge
 * Small pill showing whether the passkey is synced across devices or
 * bound to a single device.
 */
function DeviceTypeBadge({ deviceType }) {
    const isSynced = deviceType === "multiDevice";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                isSynced
                    ? "bg-green-50 text-green-700"
                    : "bg-gray-100 text-gray-600"
            }`}>
            {isSynced ? (
                <ShieldCheck className="h-3 w-3" />
            ) : (
                <Monitor className="h-3 w-3" />
            )}
            {isSynced ? "Synced" : "Device-bound"}
        </span>
    );
}

/**
 * PasskeyRow
 * A single passkey entry with inline rename and a delete trigger.
 */
function PasskeyRow({ passkey, onDeleteClick }) {
    const [isEditing, setIsEditing] = useState(false);
    const [draftName, setDraftName] = useState(passkey.friendlyName ?? "");
    const { mutate: renamePasskey, isPending: isRenaming } = useRenamePasskey();

    const handleRenameConfirm = () => {
        const trimmed = draftName.trim();
        if (!trimmed || trimmed === passkey.friendlyName) {
            setIsEditing(false);
            setDraftName(passkey.friendlyName ?? "");
            return;
        }
        renamePasskey(
            { id: passkey.id, friendlyName: trimmed },
            { onSuccess: () => setIsEditing(false) },
        );
    };

    const handleRenameCancel = () => {
        setIsEditing(false);
        setDraftName(passkey.friendlyName ?? "");
    };

    return (
        <li className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:flex-row sm:items-center sm:justify-between">
            {/* Left — icon + details */}
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50">
                    <Fingerprint className="h-4 w-4 text-[#2546A1]" />
                </div>

                <div className="min-w-0 space-y-1">
                    {/* Inline rename input or display name */}
                    {isEditing ? (
                        <div className="flex items-center gap-2">
                            <input
                                autoFocus
                                value={draftName}
                                onChange={(e) => setDraftName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleRenameConfirm();
                                    if (e.key === "Escape") handleRenameCancel();
                                }}
                                disabled={isRenaming}
                                maxLength={100}
                                className="h-8 rounded-lg border border-[#2546A1]/30 bg-blue-50/40 px-3 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#2546A1]/20"
                            />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        size="icon"
                                        onClick={handleRenameConfirm}
                                        disabled={isRenaming}
                                        className="cursor-pointer h-7 w-7 rounded-full bg-[#2546A1] hover:bg-[#1e3a8a]">
                                        {isRenaming ? (
                                            <Spinner className="h-3.5 w-3.5 text-white" />
                                        ) : (
                                            <Check className="h-3.5 w-3.5" />
                                        )}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Save rename</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={handleRenameCancel}
                                        disabled={isRenaming}
                                        className="cursor-pointer h-7 w-7 rounded-full">
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Cancel rename</TooltipContent>
                            </Tooltip>
                        </div>
                    ) : (
                        <p className="truncate text-sm font-semibold text-gray-900">
                            {passkey.friendlyName ?? "Unnamed passkey"}
                        </p>
                    )}

                    {/* Badge + dates */}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                        <DeviceTypeBadge deviceType={passkey.deviceType} />
                        <span>Added {formatDate(passkey.createdAt)}</span>
                        <span>·</span>
                        <span>Last used {formatDate(passkey.lastUsedAt)}</span>
                    </div>
                </div>
            </div>

            {/* Right — action buttons (hidden while inline editing) */}
            {!isEditing && (
                <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer flex items-center gap-1.5 rounded-full text-xs">
                        <Pencil className="h-3 w-3" />
                        Rename
                    </Button>

                    <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteClick(passkey)}
                        className="cursor-pointer flex items-center gap-1.5 rounded-full text-xs">
                        <Trash2 className="h-3 w-3" />
                        Remove
                    </Button>
                </div>
            )}
        </li>
    );
}

/**
 * PasskeyList
 *
 * Lists all registered passkeys for the authenticated user.
 * Used inside the account settings page alongside a "Register new passkey"
 * button that opens <RegisterPasskeyModal />.
 */
export default function PasskeyList() {
    const { data: passkeys, isLoading, isError } = useUserPasskeys();
    const [passkeyToDelete, setPasskeyToDelete] = useState(null);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-400">
                <Spinner className="h-5 w-5 text-[#2546A1]" />
                <span className="ml-2 text-sm">Loading passkeys...</span>
            </div>
        );
    }

    if (isError) {
        return (
            <p className="py-6 text-center text-sm text-red-500">
                Failed to load passkeys. Please refresh and try again.
            </p>
        );
    }

    if (!passkeys?.length) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                    <Fingerprint className="h-6 w-6 text-[#2546A1]/50" />
                </div>
                <p className="text-sm font-medium text-gray-500">
                    No passkeys registered yet
                </p>
                <p className="max-w-xs text-xs text-gray-400">
                    Register a passkey to sign in with your fingerprint, face, or a
                    security key — no password needed.
                </p>
            </div>
        );
    }

    return (
        <>
            <ul className="space-y-3">
                {passkeys.map((passkey) => (
                    <PasskeyRow
                        key={passkey.id}
                        passkey={passkey}
                        onDeleteClick={setPasskeyToDelete}
                    />
                ))}
            </ul>

            {/* Delete confirmation dialog — rendered once, driven by state */}
            <DeletePasskeyDialog
                open={!!passkeyToDelete}
                onOpenChange={(isOpen) => {
                    if (!isOpen) setPasskeyToDelete(null);
                }}
                passkey={passkeyToDelete}
            />
        </>
    );
}